import React, { useState, useEffect, useRef } from 'react';
import { Input } from './input';
import { Card, CardContent } from './card';
import { Badge } from './badge';
import { Button } from './button';
import { Separator } from './separator';
import { Building2, MapPin, Phone, Mail, ChevronDown, Check, Database, Plus } from 'lucide-react';
import { RaynetCompany, AresCompany, CompanySearchResult } from '@/api/types';
import { raynetClient } from '@/api/raynetClient';
import { cn } from '@/lib/utils';

interface CompanySearchProps {
  value: string;
  onChange: (value: string) => void;
  onCompanySelect?: (company: RaynetCompany) => void;
  onAresCompanySelect?: (company: AresCompany) => void;
  onAddToRaynet?: (aresCompany: AresCompany) => Promise<RaynetCompany>;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  country?: string; // Kód země - ARES search funguje pouze pro 'CZ'
}

export const CompanySearch: React.FC<CompanySearchProps> = ({
  value,
  onChange,
  onCompanySelect,
  onAresCompanySelect,
  onAddToRaynet,
  placeholder = "Začněte psát název firmy...",
  className,
  disabled = false,
  country = 'CZ'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [companies, setCompanies] = useState<CompanySearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<RaynetCompany | null>(null);
  const [selectedAresCompany, setSelectedAresCompany] = useState<AresCompany | null>(null);
  const [searchQuery, setSearchQuery] = useState(value);
  const [isUserInput, setIsUserInput] = useState(false);
  
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();

  // Debounced search - trigger when user is typing or when search query is long enough
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Search when query is long enough, regardless of how it was set
    if (searchQuery.length >= 2) {
      setIsLoading(true);
      debounceRef.current = setTimeout(async () => {
        try {
          let results: CompanySearchResult[] = [];
          
          // Vždy hledej v Raynetu
          const raynetCompanies = await raynetClient.searchCompanies(searchQuery);
          const raynetResults: CompanySearchResult[] = raynetCompanies.map(company => ({
            ...company,
            source: 'RAYNET' as const
          }));
          results.push(...raynetResults);

          // ARES search pouze pro Českou republiku
          if (country === 'CZ' && raynetResults.length < 3) {
            console.log('Doplňuji výsledky z ARES (pouze pro ČR)...');
            const aresCompanies = await raynetClient.searchAresCompanies(searchQuery);
            
            // Transform ARES results and filter out duplicates by ICO
            const existingIcos = new Set(raynetResults.map(r => r.ico).filter(Boolean));
            
            const aresResults: CompanySearchResult[] = aresCompanies
              .filter(company => !existingIcos.has(company.ico))
              .map(company => ({
                ...company,
                source: 'ARES' as const
              }));
            
            results.push(...aresResults);
          }

          setCompanies(results);
          // Only show dropdown if this is user input to avoid programmatic showing
          if (isUserInput) {
            setIsOpen(results.length > 0);
          }
        } catch (error) {
          console.error('Chyba při vyhledávání:', error);
          setCompanies([]);
        } finally {
          setIsLoading(false);
        }
      }, 300);
    } else {
      setCompanies([]);
      setIsOpen(false);
      setIsLoading(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery, isUserInput, country]);

  // Synchronize searchQuery with external value changes (programmatic, not user input)
  useEffect(() => {
    // Only sync if the value is different from current searchQuery to avoid overriding user input
    if (value !== searchQuery) {
      setSearchQuery(value);
      // Only reset user input flag if this is truly an external change (not user typing)
      // If searchQuery is empty and value has content, it's likely a programmatic set
      if (searchQuery === '' && value !== '') {
        setIsUserInput(false);
      }
    }
  }, [value, searchQuery, isUserInput]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    setIsUserInput(true); // Mark as user input to trigger search
    onChange(newValue);
    
    // Clear selection if user is typing something different
    if (selectedCompany && newValue !== selectedCompany.name) {
      setSelectedCompany(null);
    }
    if (selectedAresCompany && newValue !== selectedAresCompany.name) {
      setSelectedAresCompany(null);
    }
  };

  const handleRaynetCompanySelect = (company: RaynetCompany) => {
    setSelectedCompany(company);
    setSelectedAresCompany(null);
    setSearchQuery(company.name);
    onChange(company.name);
    setIsOpen(false);
    onCompanySelect?.(company);
    
    // Focus input after selection
    inputRef.current?.focus();
  };

  const handleAresCompanySelect = (company: AresCompany) => {
    setSelectedAresCompany(company);
    setSelectedCompany(null);
    setSearchQuery(company.name);
    onChange(company.name);
    setIsOpen(false);
    onAresCompanySelect?.(company);
    
    // Focus input after selection
    inputRef.current?.focus();
  };

  const handleAddToRaynet = async (aresCompany: AresCompany) => {
    if (!onAddToRaynet) return;
    
    try {
      const raynetCompany = await onAddToRaynet(aresCompany);
      handleRaynetCompanySelect(raynetCompany);
    } catch (error) {
      console.error('Chyba při přidávání do Raynetu:', error);
    }
  };

  const handleInputFocus = () => {
    // Mark as user input when user focuses the field
    setIsUserInput(true);
    
    // Show dropdown if we have results for current query
    if (companies.length > 0 && searchQuery.length >= 2) {
      setIsOpen(true);
    }
  };

  const formatAddress = (addresses?: RaynetCompany['addresses']) => {
    if (!addresses || addresses.length === 0) return null;
    
    // Use primary address or first available
    const address = addresses.find(addr => addr.isPrimary) || addresses[0];
    
    const parts = [
      address.street,
      address.city,
      address.zipCode
    ].filter(Boolean);
    
    return parts.join(', ');
  };

  return (
    <div ref={searchRef} className={cn("relative w-full", className)}>
      <div className="relative">
        <Input
          ref={inputRef}
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "pr-10 transition-all duration-200",
            selectedCompany && "border-green-300 focus:border-green-500",
            selectedAresCompany && "border-orange-300 focus:border-orange-500",
            isLoading && "opacity-70"
          )}
        />
        
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-400 border-t-transparent" />
          ) : selectedCompany ? (
            <Check className="h-4 w-4 text-green-500" />
          ) : selectedAresCompany ? (
            <Check className="h-4 w-4 text-orange-500" />
          ) : (
            <ChevronDown className={cn(
              "h-4 w-4 text-slate-400 transition-transform duration-200",
              isOpen && "rotate-180"
            )} />
          )}
        </div>
      </div>

      {/* Selected company info */}
      {selectedCompany && !isOpen && (
        <Card className="mt-2 border-green-200 bg-green-50">
          <CardContent className="p-3">
            <div className="flex items-start gap-3">
              <Building2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-green-800">{selectedCompany.name}</span>
                  <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                    🎯 V Raynetu
                  </Badge>
                </div>
                
                {selectedCompany.ico && (
                  <p className="text-sm text-green-600 mb-1">IČO: {selectedCompany.ico}</p>
                )}
                
                {selectedCompany.addresses && selectedCompany.addresses.length > 0 && (
                  <div className="flex items-center gap-1 text-sm text-green-600 mb-1">
                    <MapPin className="h-3 w-3" />
                    <span>{formatAddress(selectedCompany.addresses)}</span>
                  </div>
                )}
                
                <div className="flex flex-wrap gap-3 text-sm text-green-600">
                  {selectedCompany.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      <span>{selectedCompany.phone}</span>
                    </div>
                  )}
                  {selectedCompany.email && (
                    <div className="flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      <span>{selectedCompany.email}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selected ARES company info */}
      {selectedAresCompany && !isOpen && (
        <Card className="mt-2 border-orange-200 bg-orange-50">
          <CardContent className="p-3">
            <div className="flex items-start gap-3">
              <Building2 className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-orange-800">{selectedAresCompany.name}</span>
                  <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700">
                    📋 Z ARES
                  </Badge>
                  {onAddToRaynet && (
                    <Button
                      onClick={() => handleAddToRaynet(selectedAresCompany)}
                      size="sm"
                      variant="outline"
                      className="text-orange-600 border-orange-300 hover:bg-orange-100 h-6 px-2 ml-auto"
                    >
                      <Plus size={12} className="mr-1" />
                      Přidat do Raynet
                    </Button>
                  )}
                </div>
                
                <div className="flex items-center gap-4 text-sm text-orange-600 mb-1">
                  {selectedAresCompany.ico && (
                    <span>IČO: {selectedAresCompany.ico}</span>
                  )}
                  {selectedAresCompany.legalForm && (
                    <Badge variant="secondary" className="text-xs">
                      {selectedAresCompany.legalForm}
                    </Badge>
                  )}
                  <span className={`${selectedAresCompany.active ? 'text-green-600' : 'text-red-600'}`}>
                    {selectedAresCompany.active ? '✅ Aktivní' : '❌ Neaktivní'}
                  </span>
                </div>
                
                {selectedAresCompany.address && (
                  <div className="flex items-center gap-1 text-sm text-orange-600">
                    <MapPin className="h-3 w-3" />
                    <span>
                      {selectedAresCompany.address.street}, {selectedAresCompany.address.zipCode} {selectedAresCompany.address.city}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search results dropdown */}
      {isOpen && companies.length > 0 && (
        <Card className="absolute z-50 w-full mt-1 max-h-80 overflow-y-auto shadow-lg border-slate-200">
          <CardContent className="p-0">
            {(() => {
              const raynetCompanies = companies.filter(c => c.source === 'RAYNET');
              const aresCompanies = companies.filter(c => c.source === 'ARES');
              
              return (
                <>
                  {/* Raynet results */}
                  {raynetCompanies.length > 0 && (
                    <>
                      {raynetCompanies.map((company) => (
                        <div
                          key={`raynet-${company.id}`}
                          onClick={() => handleRaynetCompanySelect(company as RaynetCompany)}
                          className="p-3 hover:bg-green-50 cursor-pointer border-b border-slate-100 transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <Building2 className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-slate-800 truncate">{company.name}</span>
                                <Badge variant="outline" className="text-xs border-green-300 text-green-700">
                                  🎯 Raynet
                                </Badge>
                              </div>
                              
                              {company.ico && (
                                <p className="text-xs text-slate-500 mb-1">IČO: {company.ico}</p>
                              )}
                              
                              {company.addresses && formatAddress(company.addresses) && (
                                <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                                  <MapPin className="h-3 w-3" />
                                  <span className="truncate">{formatAddress(company.addresses)}</span>
                                </div>
                              )}
                              
                              {(company.phone || company.email) && (
                                <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                                  {company.phone && (
                                    <div className="flex items-center gap-1">
                                      <Phone className="h-3 w-3" />
                                      <span>{company.phone}</span>
                                    </div>
                                  )}
                                  {company.email && (
                                    <div className="flex items-center gap-1">
                                      <Mail className="h-3 w-3" />
                                      <span className="truncate">{company.email}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {aresCompanies.length > 0 && <Separator />}
                    </>
                  )}
                  
                  {/* ARES results */}
                  {aresCompanies.length > 0 && (
                    <>
                      {aresCompanies.length > 0 && raynetCompanies.length > 0 && (
                        <div className="p-2 bg-slate-50 border-b border-slate-200">
                          <div className="flex items-center gap-2 text-xs text-slate-600">
                            <Database className="h-3 w-3" />
                            <span>Firmy z obchodního rejstříku (ARES)</span>
                          </div>
                        </div>
                      )}
                      
                      {aresCompanies.map((company) => (
                        <div
                          key={`ares-${company.ico}`}
                          onClick={() => handleAresCompanySelect(company as AresCompany)}
                          className="p-3 hover:bg-orange-50 cursor-pointer border-b border-slate-100 last:border-b-0 transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <Building2 className="h-4 w-4 text-orange-600 mt-1 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-slate-800 truncate">{company.name}</span>
                                <Badge variant="outline" className="text-xs border-orange-300 text-orange-700">
                                  📋 ARES
                                </Badge>
                              </div>
                              
                              {company.ico && (
                                <p className="text-xs text-slate-500 mb-1">IČO: {company.ico}</p>
                              )}
                              
                              {company.address && (
                                <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                                  <MapPin className="h-3 w-3" />
                                  <span className="truncate">
                                    {company.address.street}, {company.address.zipCode} {company.address.city}
                                  </span>
                                </div>
                              )}
                              
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-xs">
                                  {company.legalForm && (
                                    <Badge variant="secondary" className="text-xs">
                                      {company.legalForm}
                                    </Badge>
                                  )}
                                  <span className={`${company.active ? 'text-green-600' : 'text-red-600'}`}>
                                    {company.active ? '✅ Aktivní' : '❌ Neaktivní'}
                                  </span>
                                </div>
                                
                                {onAddToRaynet && (
                                  <Button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddToRaynet(company as AresCompany);
                                    }}
                                    size="sm"
                                    variant="outline"
                                    className="text-orange-600 border-orange-300 hover:bg-orange-50 h-6 px-2"
                                  >
                                    <Plus size={12} className="mr-1" />
                                    Přidat do Raynet
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </>
              );
            })()}
          </CardContent>
        </Card>
      )}
    </div>
  );
}; 